import React, { useEffect, useRef, useState, useCallback } from "react";
import styled from "styled-components";
import { PORTAL_IMAGE } from "constants/imagePath";
import { supportedLangs, supportedLangsMap, supportedLangsArray } from "../../i18n";
import { useDeviceMatch } from "../../hooks/useDeviceMatch";
import { useTranslation } from "react-i18next";

// Styled components
const LanguageSwitcherContainer = styled.div`
    position: relative;
    margin-right: 20px;
    z-index: 100;
`;

const SelectedLanguage = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: ${props => (props.isMobile ? "4px 8px" : "6px 12px")};
    border-radius: 20px;
    background-color: ${props => props.theme.colours.hpWhite.hex};
    box-shadow: ${props => props.theme.portalShadows.card1};
    border: 1px solid ${props => props.theme.colours.hpPrimaryPurple.hex};
    transition: all 0.2s ease;

    &:hover {
        background-color: ${props => props.theme.colours.hpGrayishBlue.hex};
    }
`;

const FlagIcon = styled.img`
    width: ${props => (props.isMobile ? "18px" : "20px")};
    height: ${props => (props.isMobile ? "13.5px" : "15px")};
    margin-right: ${props => (props.isMobile ? "0" : "8px")};
    border-radius: 2px;
    object-fit: cover;
`;

const LanguageCode = styled.span`
    font-size: ${props => props.theme.fontSize[12].px};
    font-weight: ${props => props.theme.fontWeight.intermediate};
    color: ${props => props.theme.colours.hpPrimaryPurple.hex};
`;

const ChevronIcon = styled.img`
    width: 12px;
    height: 12px;
    margin-left: 8px;
    transition: transform 0.2s ease;
    transform: ${props => (props.isOpen ? "rotate(180deg)" : "rotate(0)")};
`;

const DropdownMenu = styled.div`
    position: absolute;
    top: 100%;
    right: ${props => (props.isMobile ? "auto" : "0")};
    left: ${props => (props.isMobile ? "0" : "auto")};
    margin-top: 5px;
    width: ${props => (props.isMobile ? "150px" : "180px")};
    background-color: white;
    border-radius: 5px;
    box-shadow: ${props => props.theme.portalShadows.card1};
    border: 2px solid ${props => props.theme.colours.hpLightGray.hex};
    overflow: hidden;
    z-index: 100;
    max-height: 200px;
    overflow-y: auto;
`;

const LanguageOption = styled.div`
    display: flex;
    align-items: center;
    padding: 10px 15px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    background-color: ${props => (props.isActive ? props.theme.colours.hpGrayishBlue.hex : "white")};

    &:hover {
        background-color: ${props => props.theme.colours.hpGrayishBlue.hex};
    }
`;

const LanguageName = styled.span`
    font-size: ${props => props.theme.fontSize[12].px};
    font-weight: ${props => (props.isActive ? props.theme.fontWeight.semiBold : props.theme.fontWeight.regular)};
    color: ${props => props.theme.colours.hpDesaturatedBlue.hex};
    margin-left: 10px;
`;

/**
 * Enhanced language switcher component with dropdown and flag icons
 */
const LanguageSwitcher = () => {
    const { i18n, t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const isSmallDevice = useDeviceMatch(["mobile", "tablet"]);

    // Function to update the URL's 'lng' query parameter
    const updateUrlLanguage = useCallback(languageCode => {
        if (typeof window !== "undefined") {
            const params = new URLSearchParams(window.location.search);
            params.set("lng", languageCode);

            // Construct the new URL path
            const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;

            // Update the URL without causing a full page reload
            window.history.pushState({ path: newUrl }, "", newUrl);
        }
    }, []);

    // Effect to ensure the current i18n language is reflected in the URL on mount/change
    useEffect(() => {
        updateUrlLanguage(i18n.language);
    }, [i18n.language, updateUrlLanguage]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = event => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef]);

    // Toggle dropdown
    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const handleLanguageChange = languageCode => {
        // 1. Change the application language state
        i18n.changeLanguage(languageCode);

        // 2. Update the URL query parameter
        updateUrlLanguage(languageCode);

        // 3. Close the dialog
        setIsOpen(false);
    };

    const getCurrentLanguageName = () => {
        const currentLang = i18n.language;
        return supportedLangsMap[currentLang]?.localName || supportedLangsMap[currentLang]?.name || currentLang;
    };

    const currentLanguage = getCurrentLanguageName();

    return (
        <LanguageSwitcherContainer ref={dropdownRef}>
            <SelectedLanguage isMobile={isSmallDevice} onClick={toggleDropdown} data-testid="language-switcher">
                <FlagIcon isMobile={isSmallDevice} src={supportedLangsMap[i18n.language]?.flag} alt={currentLanguage} />
                {!isSmallDevice && <LanguageCode>{t("buttons.change_language", "Choose Language")}</LanguageCode>}
                {!isSmallDevice && (
                    <ChevronIcon
                        src={`${PORTAL_IMAGE}/authenticatedHeader/Chevrons_chevron-down.svg`}
                        isOpen={isOpen}
                    />
                )}
            </SelectedLanguage>

            {isOpen && (
                <DropdownMenu isMobile={isSmallDevice} data-testid="language-dropdown">
                    {supportedLangs.map(language => {
                        if (language.code === currentLanguage) {
                            return null;
                        } else {
                            return (
                                <LanguageOption
                                    key={language.code}
                                    isActive={currentLanguage === language.code}
                                    onClick={() => handleLanguageChange(language.code)}
                                    data-testid={`language-option-${language.code}`}
                                >
                                    <FlagIcon isMobile={isSmallDevice} src={language?.flag} alt={language.localName} />
                                    <LanguageName isActive={currentLanguage === language.code}>
                                        {language.localName}
                                    </LanguageName>
                                </LanguageOption>
                            );
                        }
                    })}
                </DropdownMenu>
            )}
        </LanguageSwitcherContainer>
    );
};

export default LanguageSwitcher;
